import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, User, Menu, X } from "lucide-react";
import { navLinks } from "../mock";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./LanguageSwitcher";

const Logo = () => (
  <Link to="/" className="flex items-center gap-2.5 group" data-testid="header-logo-link">
    <svg width="28" height="28" viewBox="-3 0 28 28" className="shrink-0 text-[#0057FF]" aria-hidden="true" data-testid="header-logo-mark">
      <path d="M20 5.5 A12 12 0 1 0 20 22.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="21.5" cy="14" r="2.4" fill="currentColor" />
    </svg>
    <span className="font-display font-extrabold tracking-[0.18em] text-lg" data-testid="header-logo-name">ORICO</span>
  </Link>
);

const Header = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname, i18n.resolvedLanguage]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#f2f2f0]/90 backdrop-blur-md border-b border-black/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        <Logo />

        <nav data-testid="desktop-navigation" className="hidden lg:flex items-center gap-5 xl:gap-9">
          {navLinks.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              data-testid={`header-nav-${l.key}`}
              aria-current={location.pathname === l.path ? "page" : undefined}
              className={`text-[11px] font-medium uppercase tracking-[0.14em] link-underline transition-colors ${
                location.pathname === l.path ? "text-[#0e0e10]" : "text-[#0e0e10]/55 hover:text-[#0e0e10]"
              }`}
            >
              {t(`nav.${l.key}`)}
            </Link>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
          <LanguageSwitcher />
          <button data-testid="header-search-button" aria-label={t("common.search")} className="hidden md:flex w-10 h-10 rounded-full bg-[#0e0e10] text-white items-center justify-center hover:bg-[#0057FF] transition-colors duration-300">
            <Search size={16} />
          </button>
          <Link
            to="/kontakt"
            data-testid="header-account-link"
            className="hidden sm:flex items-center gap-2 h-10 px-4 rounded-full border border-black/25 text-[11px] font-medium uppercase tracking-[0.14em] hover:bg-[#0e0e10] hover:text-white transition-colors duration-300"
          >
            <User size={14} /> {t("common.login")}
          </Link>
          <button data-testid="mobile-menu-toggle" aria-label={t(open ? "common.menuClose" : "common.menuOpen")} aria-expanded={open} aria-controls="mobile-navigation" className="lg:hidden w-10 h-10 flex items-center justify-center" onClick={() => setOpen(!open)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <nav id="mobile-navigation" data-testid="mobile-navigation" className="lg:hidden bg-[#f2f2f0] border-t border-black/10 px-5 py-6 flex flex-col gap-5">
          {navLinks.map((l) => (
            <Link key={l.path} to={l.path} data-testid={`mobile-nav-${l.key}`} aria-current={location.pathname === l.path ? "page" : undefined} className="text-sm font-medium uppercase tracking-[0.14em]">
              {t(`nav.${l.key}`)}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
};

export default Header;
